import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./style.module.css";
import { pluralizeDevices } from "../../utils/pluralizeDevices";

interface DeviceSliderProps {
  options: number[];
  value: number;
  onChange: (value: number) => void;
  /** Показать подписи значений под треком. */
  showLabels?: boolean;
  /**
   * Визуальные деления шкалы (равные интервалы).
   * По умолчанию `[0, ...options]` — ноль только на шкале, выбрать его нельзя.
   */
  stops?: number[];
}

const THUMB_SIZE = 24;
/**
 * Внутреннее разрешение нативного range. С шагом «1 на стоп» (0..3 для
 * DEVICE_LIMITS) браузер двигает нативный thumb большими прыжками — только
 * 4 позиции на всю ширину трека, тач-драг ощущается как «дёргается».
 * Гоняем range по мелкой шкале 0..RESOLUTION и сами определяем ближайший
 * стоп — палец ведёт thumb непрерывно, наружу отдаём только ближайшее
 * значение из options.
 */
const RESOLUTION = 1000;

function DeviceSlider({ options, value, onChange, showLabels = false, stops }: DeviceSliderProps) {
  const scale = stops ?? [0, ...options];
  const lastIndex = scale.length - 1;
  // Индекс 0 (деление «0») выбрать нельзя — минимально доступный стоп — первый option.
  const minIndex = lastIndex > 0 ? 1 : 0;

  const indexToRaw = (idx: number) => (lastIndex > 0 ? Math.round((idx / lastIndex) * RESOLUTION) : 0);
  const rawToNearestIndex = (r: number) =>
    lastIndex > 0 ? Math.min(lastIndex, Math.max(minIndex, Math.round((r / RESOLUTION) * lastIndex))) : 0;
  const valueToRaw = (v: number) => indexToRaw(Math.max(0, scale.indexOf(v)));

  const minRaw = indexToRaw(minIndex);

  const [raw, setRaw] = useState(() => Math.max(minRaw, valueToRaw(value)));
  const [dragging, setDragging] = useState(false);
  const lastEmitted = useRef(value);

  useEffect(() => {
    // Резинхронизируемся только если `value` поменялся снаружи (например,
    // модалку переоткрыли с другим лимитом) — не из-за нашего же onChange,
    // иначе thumb будет откатываться к «снапнутой» позиции стопа посреди
    // драга, стоит пальцу пересечь границу между стопами.
    if (value !== lastEmitted.current) {
      setRaw(Math.max(minRaw, valueToRaw(value)));
      lastEmitted.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const percent = lastIndex > 0 ? (raw / RESOLUTION) * 100 : 0;
  const scaleIndex = rawToNearestIndex(raw);

  const emitNearest = (r: number) => {
    if (lastIndex <= 0) return;
    const nextValue = scale[rawToNearestIndex(r)];
    if (options.includes(nextValue) && nextValue !== value) {
      lastEmitted.current = nextValue;
      onChange(nextValue);
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Зона между «0» и первым option полностью «мёртвая» — палец в ней
    // тащит thumb, а thumb не должен сдвигаться дальше положения minRaw.
    const nextRaw = Math.max(minRaw, Number(event.target.value));
    setRaw(nextRaw);
    emitNearest(nextRaw);
  };

  const snapToNearest = () => {
    setDragging(false);
    setRaw(indexToRaw(rawToNearestIndex(raw)));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (lastIndex <= 0) return;
    const currentIndex = rawToNearestIndex(raw);
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      nextIndex = Math.min(lastIndex, currentIndex + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      nextIndex = Math.max(minIndex, currentIndex - 1);
    } else if (event.key === "Home") {
      nextIndex = minIndex;
    } else if (event.key === "End") {
      nextIndex = lastIndex;
    } else {
      return;
    }
    event.preventDefault();
    const nextRaw = indexToRaw(nextIndex);
    setRaw(nextRaw);
    emitNearest(nextRaw);
  };

  const fillWidth =
    `calc(${percent / 100} * (100% - ${THUMB_SIZE}px) + ${THUMB_SIZE}px)`;
  // Тики (и скруглённые концы трека) отступают на THUMB_SIZE / 2 от краёв —
  // thumb должен центрироваться по той же формуле, иначе на крайних стопах
  // он съезжает к самому краю трека и не совпадает с меткой.
  const thumbLeft =
    `calc(${THUMB_SIZE / 2}px + ${percent / 100} * (100% - ${THUMB_SIZE}px))`;

  return (
    <div className={styles.wrap}>
      <div className={styles.track}>
        <div className={styles.trackBg} />
        <div
          className={classNames(styles.fill, { [styles.animated]: !dragging })}
          style={{ width: fillWidth }}
        />
        <div className={styles.ticks}>
          {scale.map((stop, i) => (
            <div
              key={stop}
              className={classNames(styles.tick, {
                [styles.tickActive]: i <= scaleIndex
              })}
              style={{ left: `${lastIndex > 0 ? (i / lastIndex) * 100 : 0}%` }}
            />
          ))}
        </div>
        <div
          className={classNames(styles.thumb, { [styles.animated]: !dragging })}
          style={{ left: thumbLeft }}
        />
        <input
          type="range"
          className={styles.range}
          min={0}
          max={RESOLUTION}
          step={1}
          value={raw}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onPointerDown={() => setDragging(true)}
          onPointerUp={snapToNearest}
          onPointerCancel={snapToNearest}
          aria-label={`Количество ${pluralizeDevices(value)}`}
        />
      </div>
      {showLabels && (
        <div className={styles.labels}>
          {scale.map((stop, i) => (
            <span
              key={stop}
              className={styles.label}
              style={{ left: `${lastIndex > 0 ? (i / lastIndex) * 100 : 0}%` }}
            >
              {stop}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default DeviceSlider;
